/**
 * Voice call audio.
 *
 * RustDesk carries voice as raw Opus packets in `AudioFrame` - the packets
 * themselves, not a container. WebCodecs is therefore the right tool at both
 * ends: `MediaRecorder` would hand back WebM, which the peer cannot read, and
 * `decodeAudioData` cannot take a bare Opus packet.
 *
 * The peer states its sample rate and channel count in an `AudioFormat` before
 * the first frame, so the decoder is configured from what it actually sends
 * rather than from an assumption.
 */
var _a;
const DEFAULT_FORMAT = { sampleRate: 48_000, channels: 1 };
/** True when this browser can both decode and encode Opus with WebCodecs. */
export async function isVoiceSupported() {
    if (typeof AudioDecoder === "undefined" || typeof AudioEncoder === "undefined")
        return false;
    if (typeof MediaStreamTrackProcessor === "undefined")
        return false;
    try {
        const dec = await AudioDecoder.isConfigSupported({
            codec: "opus", sampleRate: 48_000, numberOfChannels: 1,
        });
        const enc = await AudioEncoder.isConfigSupported({
            codec: "opus", sampleRate: 48_000, numberOfChannels: 1, bitrate: 32_000,
        });
        return !!dec.supported && !!enc.supported;
    }
    catch {
        return false;
    }
}
/**
 * Plays the Opus packets the machine sends.
 *
 * Decoded audio is scheduled a little ahead of the clock. Without that lead a
 * late packet lands in the past and is simply dropped, which is heard as a
 * click; with it, a brief stall is absorbed instead.
 */
export class AudioPlayer {
    #ctx = null;
    #decoder = null;
    #format = DEFAULT_FORMAT;
    /** When the next packet should start, on the context's clock. */
    #playAt = 0;
    #onError;
    #closed = false;
    /** Seconds of lead. Enough to ride out a hiccup, short enough to converse. */
    static LEAD = 0.12;
    constructor(onError = () => { }) {
        this.#onError = onError;
    }
    setFormat(format) {
        const sampleRate = format.sampleRate > 0 ? format.sampleRate : DEFAULT_FORMAT.sampleRate;
        const channels = format.channels > 0 ? format.channels : DEFAULT_FORMAT.channels;
        if (sampleRate === this.#format.sampleRate && channels === this.#format.channels
            && this.#decoder) {
            return;
        }
        this.#format = { sampleRate, channels };
        this.#configure();
    }
    #configure() {
        if (this.#closed)
            return;
        try {
            this.#decoder?.close();
        }
        catch { /* already closed */ }
        this.#decoder = null;
        if (typeof AudioDecoder === "undefined") {
            this.#onError("This browser cannot decode the machine's audio");
            return;
        }
        if (!this.#ctx) {
            this.#ctx = new AudioContext({ sampleRate: this.#format.sampleRate });
        }
        const decoder = new AudioDecoder({
            output: (frame) => this.#play(frame),
            error: (e) => this.#onError(e.message),
        });
        decoder.configure({
            codec: "opus",
            sampleRate: this.#format.sampleRate,
            numberOfChannels: this.#format.channels,
        });
        this.#decoder = decoder;
        this.#playAt = 0;
    }
    /** Feeds one Opus packet from the peer. */
    push(data) {
        if (this.#closed)
            return;
        if (!this.#decoder)
            this.#configure();
        const decoder = this.#decoder;
        if (!decoder || decoder.state !== "configured")
            return;
        try {
            decoder.decode(new EncodedAudioChunk({
                type: "key", // every Opus packet stands alone
                timestamp: performance.now() * 1000,
                data,
            }));
        }
        catch (e) {
            this.#onError(e.message);
        }
    }
    #play(frame) {
        const ctx = this.#ctx;
        if (!ctx || this.#closed) {
            frame.close();
            return;
        }
        try {
            const channels = frame.numberOfChannels;
            const samples = frame.numberOfFrames;
            const buffer = ctx.createBuffer(channels, samples, frame.sampleRate);
            for (let c = 0; c < channels; c++) {
                const plane = new Float32Array(samples);
                frame.copyTo(plane, { planeIndex: c, format: "f32-planar" });
                buffer.copyToChannel(plane, c);
            }
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            source.connect(ctx.destination);
            // Behind the clock means the stream stalled; restart the lead rather
            // than trying to catch up on audio nobody can hear any more.
            const now = ctx.currentTime;
            if (this.#playAt < now)
                this.#playAt = now + _a.LEAD;
            source.start(this.#playAt);
            this.#playAt += buffer.duration;
        }
        catch (e) {
            this.#onError(e.message);
        }
        finally {
            frame.close();
        }
    }
    /** Browsers start a context suspended until a gesture; call on the click. */
    async resume() {
        try {
            await this.#ctx?.resume();
        }
        catch { /* nothing to resume */ }
    }
    close() {
        this.#closed = true;
        try {
            this.#decoder?.close();
        }
        catch { /* already closed */ }
        try {
            void this.#ctx?.close();
        }
        catch { /* already closed */ }
        this.#decoder = null;
        this.#ctx = null;
    }
}
_a = AudioPlayer;
/**
 * Captures the microphone and hands back Opus packets.
 *
 * 20 ms frames at 32 kbit/s: RustDesk's own settings, and what keeps a call
 * responsive rather than merely intelligible.
 */
export class AudioCapture {
    #stream = null;
    #encoder = null;
    #reader = null;
    #stopped = false;
    /**
     * Starts capturing. Resolves once the microphone is live; rejects when
     * permission is refused or the browser cannot encode Opus.
     */
    async start(onPacket, onError = () => { }) {
        if (typeof AudioEncoder === "undefined" || typeof MediaStreamTrackProcessor === "undefined") {
            throw new Error("This browser cannot encode audio for a call");
        }
        this.#stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                channelCount: 1,
                sampleRate: 48_000,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
            },
        });
        const track = this.#stream.getAudioTracks()[0];
        if (!track)
            throw new Error("No microphone was available");
        const encoder = new AudioEncoder({
            output: (chunk) => {
                const buf = new Uint8Array(chunk.byteLength);
                chunk.copyTo(buf);
                onPacket(buf);
            },
            error: (e) => onError(e.message),
        });
        encoder.configure({
            codec: "opus",
            sampleRate: 48_000,
            numberOfChannels: 1,
            bitrate: 32_000,
            opus: { frameDuration: 20_000 },
        });
        this.#encoder = encoder;
        const processor = new MediaStreamTrackProcessor({ track });
        this.#reader = processor.readable.getReader();
        void (async () => {
            const reader = this.#reader;
            if (!reader)
                return;
            try {
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done || this.#stopped) {
                        value?.close();
                        break;
                    }
                    if (encoder.state === "configured")
                        encoder.encode(value);
                    value.close();
                }
            }
            catch (e) {
                if (!this.#stopped)
                    onError(e.message);
            }
        })();
    }
    stop() {
        this.#stopped = true;
        try {
            void this.#reader?.cancel();
        }
        catch { /* already done */ }
        try {
            this.#encoder?.close();
        }
        catch { /* already closed */ }
        for (const track of this.#stream?.getTracks() ?? [])
            track.stop();
        this.#reader = null;
        this.#encoder = null;
        this.#stream = null;
    }
}
